// /web/src/components/AppLayout.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { ConsentProvider, useConsent } from "../consent/ConsentContext";
import CookieBanner from "../consent/CookieBanner";
import CookieSettingsModal from "../consent/CookieSettingsModal";
import Logo from "./Logo.jsx";

import { useApi } from "../lib/api";
import {
  identifyAmplitudeUser,
  resetAmplitudeIdentity,
  setAmplitudeConsent,
  trackPageView,
} from "../lib/amplitude";

const CALCULATOR_LINKS = [
  { to: "/calculators/lifts", label: "Lift Traffic" },
  { to: "/calculators/wc", label: "WC Provision" },
  { to: "/calculators/stairs", label: "Stairs" },
  { to: "/calculators/risers", label: "Risers" },
  { to: "/calculators/u-values", label: "Envelope" },
  { to: "/calculators/cycles", label: "Cycles" },
];

const UPCOMING_CALCULATORS = [
  { label: "Plant" },
  { label: "End of trip" },
];

const HISTORY_LABELS = {
  wc: "WC Provision",
  lifts: "Lift Traffic",
  stairs: "Stairs",
  risers: "Riser strategy",
  plant: "Plant",
  u_values: "Envelope",
  cycles: "Cycle parking",
  end_of_trip: "End of trip",
  planning: "Planning",
};

function calcHistoryPath(event) {
  if (!event) return "/dashboard";
  const slug = event.calc_type?.replace(/_/g, "-") ?? "";
  if (!slug) return "/dashboard";
  const usageQuery = event.id ? `?usage_event_id=${encodeURIComponent(event.id)}` : "";
  if (event.project_id) {
    return `/projects/${event.project_id}/calculators/${slug}${usageQuery}`;
  }
  return `/calculators/${slug}${usageQuery}`;
}

function prettyCalc(event) {
  if (!event) return "Calculator";
  const key = String(event.calc_type || "").toLowerCase();
  return HISTORY_LABELS[key] || event.calc_type || "Calculator";
}

function historySummary(event) {
  const type = event?.calc_type;
  const meta = event?.metadata || {};
  if (type === "lifts") {
    const parts = [];
    if (typeof meta.num_lifts === "number") {
      parts.push(`${meta.num_lifts} lifts`);
    }
    if (typeof meta.avg_wait_s === "number") {
      parts.push(`${Math.round(meta.avg_wait_s)}s avg wait`);
    }
    if (parts.length) return parts.join(" • ");
  }
  if (type === "wc") {
    const parts = [];
    if (typeof meta.floors === "number" && meta.floors > 0) {
      parts.push(`${meta.floors} floors`);
    }
    const male = typeof meta.male_wcs === "number" ? meta.male_wcs : null;
    const female = typeof meta.female_wcs === "number" ? meta.female_wcs : null;
    if (male !== null || female !== null) {
      parts.push(`Male ${male ?? "—"} / Female ${female ?? "—"}`);
    }
    if (typeof meta.accessible_wcs === "number") {
      parts.push(`${meta.accessible_wcs} accessible`);
    }
    if (parts.length) return parts.join(" • ");
  }
  return null;
}

function useAppMeta(isAuthenticated) {
  const api = useApi();
  const [plan, setPlan] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [projects, setProjects] = useState([]);
  const [history, setHistory] = useState([]);

  const loadMeta = useCallback(async () => {
    if (!isAuthenticated) {
      setPlan(null);
      setTokens(null);
      setProjects([]);
      setHistory([]);
      try {
        window.localStorage.removeItem("auth0_sub");
      } catch {
        /* ignore */
      }
      resetAmplitudeIdentity();
      return;
    }

    try {
      const [me, meTokens, projectList, recentEvents] = await Promise.all([
        api("/me"),
        api("/me/tokens"),
        api("/projects"),
        api("/usage-events/recent?limit=10&order=desc"),
      ]);

      if (me?.sub) {
        try {
          window.localStorage.setItem("auth0_sub", me.sub);
        } catch {
          /* ignore */
        }
      }

      setPlan(me?.plan ?? null);
      setTokens(
        typeof meTokens?.tokens_remaining === "number"
          ? meTokens.tokens_remaining
          : null
      );

      setProjects(Array.isArray(projectList) ? projectList.slice(0, 8) : []);
      setHistory(Array.isArray(recentEvents) ? recentEvents.slice(0, 8) : []);
    } catch {
      // API hiccups should not break the shell — leave previous state
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onTokenUpdate = (event) => {
      const remaining = event?.detail?.remaining;
      if (typeof remaining === "number") setTokens(remaining);
    };
    window.addEventListener("pd_tokens_update", onTokenUpdate);
    return () => window.removeEventListener("pd_tokens_update", onTokenUpdate);
  }, []);

  return useMemo(
    () => ({ plan, tokens, projects, history, refresh: loadMeta }),
    [plan, tokens, projects, history, loadMeta]
  );
}

function AccountMenu({ onLogout }) {
  const { user } = useAuth0();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(".pd-app-account")) return;
      setOpen(false);
    };
    window.addEventListener("pointerdown", onClick);
    return () => window.removeEventListener("pointerdown", onClick);
  }, [open]);

  return (
    <div className="pd-app-account">
      <button
        className="pd-user-btn"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {user?.name || user?.email || "Account"}
        <span aria-hidden>▾</span>
      </button>
      {open && (
        <div className="pd-user-menu" role="menu">
          <NavLink to="/dashboard" role="menuitem" onClick={() => setOpen(false)}>
            Dashboard
          </NavLink>
          <NavLink to="/billing" role="menuitem" onClick={() => setOpen(false)}>
            Billing
          </NavLink>
          <button type="button" onClick={onLogout} role="menuitem">
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

function Shell() {
  const {
    isAuthenticated,
    isLoading: authLoading,
    loginWithRedirect,
    logout,
    error,
  } = useAuth0();
  const location = useLocation();
  const { prefs } = useConsent();
  const consent = !!prefs?.analytics;
  const meta = useAppMeta(isAuthenticated);
  const [searchTerm, setSearchTerm] = useState("");
  const planLabel = (meta?.plan || "trial").toString();
  const formattedPlan = planLabel
    ? planLabel.charAt(0).toUpperCase() + planLabel.slice(1)
    : planLabel;

  const lastTrackedPath = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: location.pathname + location.search },
      });
    }
  }, [authLoading, isAuthenticated, loginWithRedirect, location.pathname, location.search]);

  useEffect(() => {
    setAmplitudeConsent(!!consent);
    if (!consent) {
      lastTrackedPath.current = null;
    }
  }, [consent]);

  useEffect(() => {
    if (!isAuthenticated || !consent) return;
    const id =
      (typeof window !== "undefined" && window.localStorage.getItem("auth0_sub")) ||
      "";
    if (!id) return;
    identifyAmplitudeUser(id, {
      plan: meta.plan || undefined,
      tokens_remaining: typeof meta.tokens === "number" ? meta.tokens : undefined,
    });
  }, [consent, isAuthenticated, meta.plan, meta.tokens]);

  useEffect(() => {
    if (!consent) return;
    const path = `${location.pathname}${location.search}`;
    if (lastTrackedPath.current === path) return;
    lastTrackedPath.current = path;
    trackPageView({
      path: location.pathname,
      search: location.search,
      title: typeof document !== "undefined" ? document.title : undefined,
    });
  }, [consent, location.pathname, location.search]);

  const [activeMenu, setActiveMenu] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const toggleMenu = (key) => {
    setActiveMenu((prev) => (prev === key ? null : key));
  };

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileNavOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileNavOpen]);

  if (authLoading) {
    return (
      <div className="pd-app pd-app__loading">
        <div className="pd-loading-card">Signing you in…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pd-app pd-app__loading" style={{ color: "crimson" }}>
        <div className="pd-loading-card">Auth error: {String(error?.message || error)}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="pd-app pd-app__loading">
        <div className="pd-loading-card">Redirecting to log in…</div>
      </div>
    );
  }

  const handleLogout = () =>
    logout({ logoutParams: { returnTo: window.location.origin } });

  const projectLinks = meta.projects.filter((p) => p?.id && p?.name);
  const historyItems = meta.history.filter((item) => item?.id);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event?.target?.value ?? "");
  }, []);

  const outletContext = useMemo(
    () => ({ ...meta, searchTerm }),
    [meta, searchTerm]
  );

  return (
    <div className="pd-app">
      <aside
        id="pd-mobile-navigation"
        className={`pd-app__sidebar${mobileNavOpen ? " pd-app__sidebar--open" : ""}`}
      >
        <div className="pd-sidebar__header">
          <h2 className="pd-sidebar__logo">
            <Link to="/" className="pd-sidebar__logo-link" aria-label="Pebbledash — Home">
              <Logo alt="Pebbledash" />
            </Link>
          </h2>
        </div>

        <nav className="pd-nav" aria-label="Primary">
          <NavLink to="/dashboard" className="pd-nav-item">
            Dashboard
          </NavLink>

          <div className="pd-nav-group">
            <button
              type="button"
              onClick={() => toggleMenu("projects")}
              className={`pd-nav-item pd-nav-item--toggle ${
                activeMenu === "projects" ? "open" : ""
              }`}
            >
              <span>Latest Projects</span>
              <span className="pd-nav-arrow" aria-hidden="true">
                <img
                  src={
                    activeMenu === "projects"
                      ? "/assets/down_arrow.svg"
                      : "/assets/side_arrow.svg"
                  }
                  alt=""
                />
              </span>
            </button>
            {activeMenu === "projects" && projectLinks.length > 0 && (
              <ul className="pd-submenu">
                {projectLinks.map((project) => (
                  <li key={project.id}>
                    <NavLink to={`/projects/${project.id}`}>{project.name}</NavLink>
                  </li>
                ))}
              </ul>
            )}
            {activeMenu === "projects" && projectLinks.length === 0 && (
              <div className="pd-submenu__empty">No recent projects yet.</div>
            )}
          </div>

          <div className="pd-nav-group">
            <button
              type="button"
              onClick={() => toggleMenu("calculators")}
              className={`pd-nav-item pd-nav-item--toggle ${
                activeMenu === "calculators" ? "open" : ""
              }`}
            >
              <span>Calculators</span>
              <span className="pd-nav-arrow" aria-hidden="true">
                <img
                  src={
                    activeMenu === "calculators"
                      ? "/assets/down_arrow.svg"
                      : "/assets/side_arrow.svg"
                  }
                  alt=""
                />
              </span>
            </button>
            {activeMenu === "calculators" && (
              <ul className="pd-submenu">
                {CALCULATOR_LINKS.map((link) => (
                  <li key={link.to}>
                    <NavLink to={link.to}>{link.label}</NavLink>
                  </li>
                ))}
                {UPCOMING_CALCULATORS.map((item) => (
                  <li key={item.label} className="pd-submenu__item--disabled">
                    <span>{item.label}</span>
                    <span className="pd-submenu__note">(coming soon)</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="pd-nav-group">
            <button
              type="button"
              onClick={() => toggleMenu("history")}
              className={`pd-nav-item pd-nav-item--toggle ${
                activeMenu === "history" ? "open" : ""
              }`}
            >
              <span>History</span>
              <span className="pd-nav-arrow" aria-hidden="true">
                <img
                  src={
                    activeMenu === "history"
                      ? "/assets/down_arrow.svg"
                      : "/assets/side_arrow.svg"
                  }
                  alt=""
                />
              </span>
            </button>
            {activeMenu === "history" && historyItems.length > 0 && (
              <ul className="pd-submenu">
                {historyItems.map((event) => (
                  <li key={event.id}>
                    {(() => {
                      const summary = historySummary(event);
                      return (
                        <NavLink to={calcHistoryPath(event)} className="pd-history-link">
                          <span className="pd-history-line pd-history-line--calc">
                            {prettyCalc(event)}
                          </span>
                          <span className="pd-history-line pd-history-line--project">
                            {event.project_name || "No project"}
                          </span>
                          {summary && (
                            <span className="pd-history-line pd-history-line--summary">
                              {summary}
                            </span>
                          )}
                        </NavLink>
                      );
                    })()}
                  </li>
                ))}
              </ul>
            )}
            {activeMenu === "history" && historyItems.length === 0 && (
              <div className="pd-submenu__empty">No calculator runs yet.</div>
            )}
          </div>

          <NavLink to="/projects/new" className="pd-nav-item">
            New Project
          </NavLink>
          <button className="pd-nav-item" type="button">
            Exports
          </button>
          <button className="pd-nav-item pd-nav-item--stacked disabled" type="button" disabled>
            <span className="pd-nav-item__title">Core Arrangement</span>
            <span className="pd-nav-item__note">Coming soon</span>
          </button>

          <hr className="pd-nav-divider" />

          <NavLink to="/billing" className="pd-nav-item">
            Settings
          </NavLink>
          <NavLink to="/contact" className="pd-nav-item">
            Contact
          </NavLink>
          <NavLink to="/blog" className="pd-nav-item">
            News
          </NavLink>
        </nav>
      </aside>

      <button
        type="button"
        className={`pd-nav-backdrop${mobileNavOpen ? " pd-nav-backdrop--visible" : ""}`}
        aria-label="Close menu"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={() => setMobileNavOpen(false)}
      />

      <div className="pd-app__main">
        <header className="pd-topbar">
          <div className="pd-topbar__start">
            <button
              type="button"
              className="pd-mobile-menu-button"
              aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
              aria-controls="pd-mobile-navigation"
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen((open) => !open)}
            >
              <span aria-hidden="true">{mobileNavOpen ? "×" : "☰"}</span>
              <span>Menu</span>
            </button>
            <input
              type="search"
              className="pd-search"
              placeholder="Search projects…"
              aria-label="Search projects"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="pd-topbar__right">
            <div className="pd-topbar__meta">
              <div className="pd-topbar__meta-item">
                <span className="pd-topbar__meta-label">Plan:</span>
                <span className="pd-topbar__meta-value">{formattedPlan}</span>
              </div>
            </div>
            <AccountMenu onLogout={handleLogout} />
          </div>
        </header>

        <div className="pd-app__content">
          <Outlet context={outletContext} />
        </div>
      </div>
    </div>
  );
}

export default function AppLayout() {
  return (
    <ConsentProvider>
      <Shell />
      <CookieBanner />
      <CookieSettingsModal />
    </ConsentProvider>
  );
}

